/* eslint-disable no-unused-expressions */
import React, { useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";
import styles from "./youtube-player.module.scss";
import classNames from "classnames/bind";
import SockJsClient from "react-stomp";
import { getHost } from "utils/APIUtils";

// Import the SVG icons
import IconPlay from "./icon-play-youtube.svg";
import IconPause from "./icon-pause-youtube.svg";
import IconReplay from "./icon-replay-youtube.svg";
const cn = classNames.bind(styles);

function YoutubePlayerWithRemocon({
  startTime = 0,
  endTime = 0,
  url = "https://www.youtube.com/watch?v=-eEvfjMQzBA",
  thumbnail,
  onEnded,
  pauseVideo = false,
  disableSeek = false,
  autoplay = true,
  earlyEndTriggerSeconds = 5,
  classroomId, // Added for remocon functionality
  isRemote = false, // Added for remocon functionality
  isOnline = true, // Added for remocon functionality
  viewType, // Added for remocon functionality
  onSendMessage, // Function to send message for remocon
}) {
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [ended, setEnded] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [loading, setLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [initialState, setInitialState] = useState(true);
  const [userActivity, setUserActivity] = useState(false);
  const [time, setTime] = useState(0); // Current time for remocon
  const [speed, setSpeed] = useState(1); // Playback speed for remocon
  const [connected, setConnected] = useState(false); // WebSocket connection status

  const playerRef = useRef(null);
  const playerWrapperRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const progressTimeoutRef = useRef(null);
  const [clickCount, setClickCount] = useState(0);
  const [hasTriggeredEarlyEnd, setHasTriggeredEarlyEnd] = useState(false);
  const [autoPlayHandled, setAutoPlayHandled] = useState(false);
  const prevPauseVideoRef = useRef(pauseVideo);
  const listenChannelRef = useRef(null);

  // Remocon WebSocket connection handlers
  const onConnect = () => {
    console.log("connect from youtube player remocon");
    setConnected(true);
  };

  const onDisconnect = () => {
    setConnected(false);
  };

  // Handle messages received from WebSocket
  const onMessageReceive = (msg, topic) => {
    console.log("receive msg: ", msg);
    try {
      // Some systems may wrap the message in different formats
      let messageToProcess = msg;
      
      // Handle remote type message wrapper
      if (msg && msg.type === "remote" && msg.data) {
        handleRemocon(msg);
      } 
      // Handle direct command messages
      else if (msg && (msg.type || (msg.data && msg.data.type))) {
        // If the command is directly in the message
        handleRemocon(msg.data ? msg : { data: msg });
      }
    } catch (error) {
      console.error("Error processing WebSocket message:", error);
    }
  };

  // Setup BroadcastChannel for remocon when not using WebSockets
  useEffect(() => {
    if (window.BroadcastChannel) {
      listenChannelRef.current = new BroadcastChannel("REMOCON_CHANNEL");
      
      // Create a wrapper to properly format messages for handleRemocon
      const handleBroadcastMessage = (event) => {
        console.log("Received broadcast message:", event.data);
        try {
          // Create a standardized format for the message handler
          const formattedMessage = { data: event.data };
          handleRemocon(formattedMessage);
        } catch (error) {
          console.error("Error processing broadcast message:", error);
        }
      };
      
      listenChannelRef.current.addEventListener("message", handleBroadcastMessage);
      
      return () => {
        if (listenChannelRef.current) {
          listenChannelRef.current.removeEventListener("message", handleBroadcastMessage);
        }
      };
    }
  }, []);

  // Progress tracking for remocon
  const progressTick = () => {
    console.log("progressTick " + time + " " + speed);

    if (!isRemote && viewType === "guide") {
      const _data = {
        type: "asyncTime",
        value: time,
        speed: speed,
      };
      if (isOnline === true && connected) {
        onSendMessage && onSendMessage(_data);
      } else if (listenChannelRef.current) {
        listenChannelRef.current.postMessage(_data);
      }
    }

    progressTimeoutRef.current = setTimeout(progressTick, 10000);
  };

  // Start progress tracking on mount
  useEffect(() => {
    if (progressTimeoutRef.current == null && viewType === "guide") {
      progressTick();
    }

    return () => {
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
      }
    };
  }, [time, speed, viewType]);

  // Handle remocon messages
  const handleRemocon = (res) => {
    try {
      if (!res) {
        console.warn("Empty remocon message received");
        return null;
      }
      
      if (!res.data) {
        // Try to handle the message directly if it doesn't have a data property
        console.warn("Non-standard remocon message format, attempting to process:", res);
        if (res.type) {
          // If it has a type directly, process it
          const directType = res.type;
          const directValue = res.value;
          console.log("Direct remocon type:", directType);
          console.log("Direct remocon value:", directValue);
          
          // Create a standardized format for processing
          res = { data: { type: directType, value: directValue } };
        } else {
          return null;
        }
      }
      
      const type = res.data.type;
      const value = res.data.value;
      console.log("remocon type: ", type);
      console.log("remocon value: ", value);
      
      if (!type) {
        return null;
      }

      switch (type) {
        case "seek":
          changeProgress(value);
          handleSeek(null, value);
          break;
        case "togglePlay":
          console.log("Received togglePlay command");
          
          try {
            // Get the actual player state directly from YouTube API
            let actualPlayerState = false;
            let youtubeState = -1;
            
            if (playerRef.current && playerRef.current.getInternalPlayer()) {
              try {
                // YouTube API PlayerState: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
                youtubeState = playerRef.current.getInternalPlayer().getPlayerState();
                actualPlayerState = youtubeState === 1; // 1 means playing
                console.log("YouTube player API state:", youtubeState, "interpreted as:", actualPlayerState ? "playing" : "paused");
              } catch (e) {
                console.error("Error getting player state:", e);
                // Fallback to React state if YouTube API fails
                actualPlayerState = playing;
              }
            } else {
              console.warn("Player ref not available, using React state");
              actualPlayerState = playing;
            }
            
            // Determine new state (opposite of current state)
            const newPlayingState = !actualPlayerState;
            console.log("togglePlay - actual current state:", actualPlayerState, 
                        "React state:", playing, 
                        "new target state:", newPlayingState);
            
            // Update React state
            setPlaying(newPlayingState);
            
            // Directly control the player
            if (playerRef.current && playerRef.current.getInternalPlayer()) {
              console.log("Controlling player directly to:", newPlayingState ? "play" : "pause");
              if (newPlayingState) {
                playerRef.current.getInternalPlayer().playVideo();
              } else {
                playerRef.current.getInternalPlayer().pauseVideo();
              }
            }
            
            // Reset trigger state when starting to play
            if (newPlayingState) {
              setHasTriggeredEarlyEnd(false);
            }
            
            // Show controls on toggle
            setShowControls(true);
            
            // Handle controls visibility timeout
            if (!newPlayingState) {
              if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
              }
            } else {
              startHideControlsTimer();
            }
            
            // If video ended, reset it
            if (ended) {
              setEnded(false);
              playerRef.current?.seekTo(startTime);
            }
          } catch (error) {
            console.error("Error in togglePlay handler:", error);
          }
          break;
        case "replay":
          handleReplay();
          break;
        case "speed":
          handleSpeedChange(value);
          break;
        case "volume":
          if (viewType === "guide" && isRemote === false) {
            return;
          } else {
            // Volume control would go here if implemented
          }
          break;
        case "progress":
          const _progress = progress + (value / duration) * 100;
          handleSeek(null, _progress);
          break;
        case "asyncTime":
          console.log("Received asyncTime command, viewType:", viewType, "diff:", value - time);
          // Time synchronization - only adjust time if difference is significant
          if (value - time >= 0.8 || value - time <= -0.8) {
            console.log("Time difference significant, syncing time");
            // If current time differs by 0.8 seconds or more
            const _progress = progress + ((value - time) / duration) * 100;
            // Use a version of handleSeek that doesn't affect playback state
            if (viewType === "preview") {
              // For preview mode, use a more careful seek that preserves current state
              if (playerRef.current) {
                console.log("Seeking preview player without affecting playback state");
                // Store the current playing state
                const wasPlaying = playing;
                const currentPlaybackRate = speed;
                
                // Perform the seek
                playerRef.current.seekTo(time + (value - time));
                
                // Ensure the state doesn't change due to the seek
                if (wasPlaying) {
                  playerRef.current?.getInternalPlayer()?.playVideo?.();
                  playerRef.current?.getInternalPlayer()?.setPlaybackRate?.(currentPlaybackRate);
                }
              }
            } else {
              // For non-preview players, use the standard seek
              handleSeek(null, _progress);
            }
          }
          
          // Speed synchronization
          if (res.data.speed !== speed && res.data.speed !== undefined) {
            console.log("Speed change requested:", res.data.speed, "current:", speed);
            handleSpeedChange(res.data.speed);
          }
          break;
        case "asyncSetting":
          console.log("Received asyncSetting command, viewType:", viewType);
          // Handle asyncSetting for both guide and preview
          if (viewType === "guide") {
            // Guide sends the current settings
            asyncSetting();
          } else if (viewType === "preview") {
            // Preview should maintain its current state, not stop playing
            console.log("Preview player receiving asyncSetting, maintaining current state");
            // No action needed - previews should continue with their current state
          }
          break;
        case "settingData":
          console.log("Received settingData command, viewType:", viewType, "value:", value);
          if (viewType !== "guide") {
            // For preview players, apply settings but with additional checks

            // Apply time synchronization if necessary
            const timeDifference = Math.abs(value.time - time);
            if (timeDifference > 0.8) {
              console.log("Synchronizing time, difference:", timeDifference);
              const _settingData = progress + ((value.time - time) / duration) * 100;
              handleSeek(null, _settingData);
            }
            
            // Apply speed change
            handleSpeedChange(value.speed);

            // Special handling for play/pause in preview mode
            if (viewType === "preview") {
              console.log("Preview player handling play state, current:", playing, "requested:", value.playing);
              
              // Check if there's a flag to keep the preview playing
              if (value.previewKeepPlaying) {
                console.log("previewKeepPlaying flag detected, maintaining current play state");
                // Don't change the playing state, just sync time and speed
              }
              // Otherwise, only update if explicitly set to false or true (not undefined)
              else if (value.playing !== undefined) {
                setPlaying(value.playing);
                
                if (value.playing) {
                  console.log("Setting preview player to play");
                  playerRef.current?.getInternalPlayer()?.playVideo?.();
                } else {
                  console.log("Setting preview player to pause");
                  playerRef.current?.getInternalPlayer()?.pauseVideo?.();
                }
              } else {
                console.log("Maintaining current play state for preview player");
              }
            } else {
              // For non-preview players, follow the guide exactly
              setPlaying(value.playing);
              
              if (value.playing) {
                playerRef.current?.getInternalPlayer()?.playVideo?.();
              } else {
                playerRef.current?.getInternalPlayer()?.pauseVideo?.();
              }
            }
          }
          break;
        case "fullScreen":
          // Full screen functionality would go here if implemented
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error processing remocon message:", error);
    }
  };

  // Send settings to other clients
  const asyncSetting = () => {
    console.log("Sending async settings, current state:", { playing, time, speed });
    const _data = {
      type: "settingData",
      value: {
        playing: playing,
        time: time,
        speed: speed,
        // Add flag to indicate this message is for preview mode clients
        // This helps preview players know how to handle the message
        previewKeepPlaying: true
      },
    };

    if (isRemote === true && connected) {
      console.log("Sending async settings via WebSocket");
      onSendMessage && onSendMessage(_data);
    } else if (listenChannelRef.current) {
      console.log("Sending async settings via BroadcastChannel");
      listenChannelRef.current.postMessage(_data);
    }
  };

  // Change playback speed and notify via remocon
  const handleSpeedChange = (newSpeed) => {
    setSpeed(parseFloat(newSpeed));

    if (playerRef.current && playerRef.current.getInternalPlayer()) {
      playerRef.current
        .getInternalPlayer()
        .setPlaybackRate(parseFloat(newSpeed));
    }

    if (viewType === "guide") {
      const _data = {
        type: "speed",
        value: newSpeed,
      };
      if (isRemote === true && connected) {
        onSendMessage && onSendMessage(_data);
      } else if (listenChannelRef.current) {
        listenChannelRef.current.postMessage(_data);
      }
    }
  };

  // Replay video and reset state
  const handleReplay = () => {
    setEnded(false);
    setHasTriggeredEarlyEnd(false);

    if (playerRef.current) {
      playerRef.current.seekTo(startTime);
      setPlaying(true);
    }

    if (viewType === "guide") {
      const _data = {
        type: "replay",
      };
      if (isRemote === true && connected) {
        onSendMessage && onSendMessage(_data);
      } else if (listenChannelRef.current) {
        listenChannelRef.current.postMessage(_data);
      }
    }
  };

  // Get video ID for thumbnail
  const getVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const videoId = getVideoId(url);
  const thumbnailUrl =
    thumbnail ||
    (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : "");

  const handlePlayPause = () => {
    if (ended) {
      handleReplay();
      return;
    }

    // Get the actual player state first
    let actualPlayerState = playing;
    if (playerRef.current && playerRef.current.getInternalPlayer()) {
      // YouTube API PlayerState: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
      const youtubeState = playerRef.current.getInternalPlayer().getPlayerState();
      actualPlayerState = youtubeState === 1; // 1 means playing
    }
    console.log("Actual YouTube player state:", actualPlayerState);
    
    // Use the actual player state to determine the new state
    const newPlayingState = !actualPlayerState;
    console.log("handlePlayPause - actual current state:", actualPlayerState, "new state:", newPlayingState);

    // Record first play
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true);
    }

    // Exit initial state when starting to play
    if (initialState && newPlayingState) {
      setInitialState(false);
    }

    // Show controls on toggle
    setShowControls(true);

    // Handle controls visibility timeout based on new state
    if (!newPlayingState) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    } else {
      startHideControlsTimer();
    }

    // Update state
    setPlaying(newPlayingState);
    
    // If the player is available, directly control it
    if (playerRef.current && playerRef.current.getInternalPlayer()) {
      if (newPlayingState) {
        playerRef.current.getInternalPlayer().playVideo();
      } else {
        playerRef.current.getInternalPlayer().pauseVideo();
      }
    }

    // Reset trigger state when starting to play
    if (newPlayingState) {
      setHasTriggeredEarlyEnd(false);
    }

    // Send toggle command via remocon
    if (viewType === "guide") {
      const _data = {
        type: "togglePlay",
      };
      if (isRemote === true && connected) {
        onSendMessage && onSendMessage(_data);
      } else if (listenChannelRef.current) {
        listenChannelRef.current.postMessage(_data);
      }
    }
  };

  const handleEnded = () => {
    if (!loop) {
      setEnded(true);
      setPlaying(false);
      playerRef.current?.seekTo(startTime);
      setShowControls(true);
    }
    // Only execute onEnded if not already triggered
    if (!hasTriggeredEarlyEnd && onEnded) {
      onEnded();
    }
  };

  const handleProgress = (state) => {
    const { playedSeconds, played } = state;
    setTime(playedSeconds); // Update current time for remocon

    // Calculate progress within the effective range
    const effectiveStartTime = startTime || 0;
    const effectiveEndTime = endTime > 0 ? endTime : duration;
    const effectiveDuration = effectiveEndTime - effectiveStartTime;

    // Calculate relative progress percentage
    const currentPosition = playedSeconds - effectiveStartTime;
    const progressPercentage =
      effectiveDuration > 0 ? (currentPosition / effectiveDuration) * 100 : 0;

    // Clamp progress to 0-100 range
    const clampedProgress = Math.max(0, Math.min(100, progressPercentage));
    setProgress(clampedProgress);

    // Check for early end trigger
    const remainingTime = effectiveEndTime - playedSeconds;
    if (
      remainingTime <= earlyEndTriggerSeconds &&
      remainingTime > 0 &&
      !hasTriggeredEarlyEnd
    ) {
      if (onEnded) {
        onEnded();
      }
      setHasTriggeredEarlyEnd(true);
    }

    // Update video state based on progress
    if (played > 0) {
      if (!videoReady) {
        setVideoReady(true);
      }
      if (loading) {
        setLoading(false);
      }
      if (buffering) {
        setBuffering(false);
      }
      if (initialState) {
        setInitialState(false);
      }
    }

    // Handle end of video
    if (endTime && playedSeconds >= endTime) {
      handleEnded();
    }
  };

  const handleSeek = (e, forcedValue = null) => {
    // If seeking is disabled, don't proceed
    if (disableSeek) return;

    let percentage;

    if (forcedValue !== null) {
      // Use the forced value directly
      percentage = forcedValue / 100;
    } else if (e) {
      // Calculate from click event
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      percentage = offsetX / rect.width;
    } else {
      // If no event or forced value, do nothing
      return;
    }

    // Calculate target time
    const effectiveStartTime = startTime || 0;
    const effectiveEndTime = endTime > 0 ? endTime : duration;
    const targetTime =
      effectiveStartTime + percentage * (effectiveEndTime - effectiveStartTime);

    // Clamp target time to valid range
    const clampedTargetTime = Math.max(
      effectiveStartTime,
      Math.min(effectiveEndTime, targetTime)
    );

    // Seek to the target time
    playerRef.current?.seekTo(clampedTargetTime);

    // Update progress display
    const progressPercentage = percentage * 100;
    const clampedProgress = Math.max(0, Math.min(100, progressPercentage));
    setProgress(clampedProgress);

    // Ensure controls are visible
    setShowControls(true);
    startHideControlsTimer();

    // Send seek command via remocon
    if (viewType === "guide") {
      const _data = {
        type: "seek",
        value: clampedProgress,
      };
      if (isRemote === true && connected) {
        onSendMessage && onSendMessage(_data);
      } else if (listenChannelRef.current) {
        listenChannelRef.current.postMessage(_data);
      }
    }
  };

  // Track progress changes for remocon functionality
  const changeProgress = (value) => {
    setProgress(value);
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  // Hide controls timer
  const startHideControlsTimer = () => {
    if (playing) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      const hideDelay = 1000;
      controlsTimeoutRef.current = setTimeout(() => {
        if (playing && !userActivity) {
          setShowControls(false);
        }
      }, hideDelay);
    }
  };

  // Handle user activity
  const handleUserActivity = () => {
    setUserActivity(true);
    setShowControls(true);

    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    activityTimeoutRef.current = setTimeout(() => {
      setUserActivity(false);
      if (playing) {
        startHideControlsTimer();
      }
    }, 1000);
  };

  const handleLoopToggle = (e) => {
    e.stopPropagation();
    setLoop(!loop);
    handleUserActivity();
  };

  const handleReady = () => {
    setVideoReady(true);
    setLoading(false);

    if (playerRef.current?.props.playing) {
      setPlaying(true);
      startHideControlsTimer();
    }

    setShowControls(true);

    if (playing) {
      startHideControlsTimer();
    }
  };

  const handleBuffer = () => {
    setBuffering(true);
    setShowControls(true);
  };

  const handleBufferEnd = () => {
    setBuffering(false);
    if (playing) {
      startHideControlsTimer();
    }
  };

  const handleError = (error) => {
    console.error("YouTube Player Error:", error);
    setLoading(false);
    setShowControls(true);
  };

  // Handle controls visibility based on playing state
  useEffect(() => {
    console.log("Playing state changed to:", playing);
    
    // Create a function to check and sync the actual player state
    const syncPlayerState = () => {
      if (playerRef.current && playerRef.current.getInternalPlayer()) {
        try {
          const youtubeState = playerRef.current.getInternalPlayer().getPlayerState();
          const isActuallyPlaying = youtubeState === 1; // 1 means playing
          
          // If there's a mismatch between React state and actual player state
          if (isActuallyPlaying !== playing) {
            console.log("Syncing player state - React state:", playing, "YouTube state:", isActuallyPlaying);
            
            // Force the player to match our React state
            if (playing) {
              playerRef.current.getInternalPlayer().playVideo();
            } else {
              playerRef.current.getInternalPlayer().pauseVideo();
            }
          }
        } catch (e) {
          console.error("Error syncing player state:", e);
        }
      }
    };
    
    // Call immediately
    syncPlayerState();
    
    // Also set a short timeout to ensure the command is processed
    const syncTimeout = setTimeout(syncPlayerState, 100);
    
    if (playing) {
      const hideDelay = 1000;

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      controlsTimeoutRef.current = setTimeout(() => {
        if (playing && !userActivity) {
          setShowControls(false);
        }
      }, hideDelay);
    } else {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
    
    // Cleanup function
    return () => {
      clearTimeout(syncTimeout);
    };
  }, [playing, userActivity]);

  // Cleanup timers on unmount
  useEffect(() => {
    const forceLoadingTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setVideoReady(true);
      }
    }, 5000);

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
      }
      clearTimeout(forceLoadingTimeout);
    };
  }, [loading]);

  // Determine if thumbnail should be shown
  const shouldShowThumbnail =
    initialState && (!hasPlayedOnce || !videoReady || !playing) && !buffering;

  // Determine if loading indicator should be shown
  const shouldShowLoadingIndicator = loading || buffering;

  // Detect touch device
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    };
    setIsTouchDevice(checkTouchDevice());
  }, []);

  // Handle click
  const handleClick = (e) => {
    console.log("Click event triggered");
    handlePlayPause();
  };

  // Handle autoplay
  useEffect(() => {
    if (playerRef.current && !autoPlayHandled) {
      if (autoplay) {
        setPlaying(true);
        startHideControlsTimer();
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 1000);
      }
      setAutoPlayHandled(true);
    }
  }, [autoPlayHandled, autoplay]);

  // Handle actual start of playback
  const handleStart = () => {
    setPlaying(true);
    setInitialState(false);
    setHasPlayedOnce(true);
    setUserActivity(false);
    startHideControlsTimer();
  };

  // Handle pause video prop changes
  useEffect(() => {
    if (prevPauseVideoRef.current !== pauseVideo) {
      if (pauseVideo) {
        setPlaying(false);
        setShowControls(true);
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      } else {
        if (videoReady && !ended) {
          setPlaying(true);
          startHideControlsTimer();
        }
      }
      prevPauseVideoRef.current = pauseVideo;
    }
  }, [pauseVideo, videoReady, ended]);

  // Handle keyboard controls
  const handleKeyDown = (e) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      handlePlayPause();
    }

    if (!disableSeek) {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const newTime = Math.min(
          endTime > 0 ? endTime : duration,
          playerRef.current.getCurrentTime() + 5
        );
        playerRef.current?.seekTo(newTime);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const newTime = Math.max(
          startTime,
          playerRef.current.getCurrentTime() - 5
        );
        playerRef.current?.seekTo(newTime);
      }
    }
  };

  // Touch event handlers
  const handleTouchStart = () => {
    handleUserActivity();
  };

  const handleTouchMove = () => {
    handleUserActivity();
  };

  const handleTouchEnd = () => {
    if (playing) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (playing && !userActivity) {
          setShowControls(false);
        }
      }, 1000);
    }
  };

  // Mouse event handlers
  const handleMouseEnter = () => {
    setShowControls(true);
    setUserActivity(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  };

  const handleMouseLeave = () => {
    setUserActivity(false);
    if (playing) {
      startHideControlsTimer();
    }
  };

  const handleMouseMove = () => {
    handleUserActivity();
  };

  return (
    <div
      className={cn("videoWrapper", {})}
      ref={playerWrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex="0"
    >
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={playing}
        className={styles.reactPlayer}
        loop={loop}
        width="100%"
        height="100%"
        onEnded={handleEnded}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onReady={handleReady}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onError={handleError}
        playsinline
        onStart={handleStart}
        config={{
          youtube: {
            playerVars: {
              autoplay: autoplay ? 1 : 0,
              controls: 0,
              showinfo: 0,
              rel: 0,
              iv_load_policy: 3,
              modestbranding: 0,
              playsinline: 1,
              enablejsapi: 1,
              fs: 1,
              color: "white",
              disablekb: 1,
              start: startTime,
            },
            embedOptions: {
              autoplay: autoplay ? 1 : 0,
              rel: 0,
              modestbranding: 0,
            },
          },
        }}
      />

      {/* Thumbnail overlay */}
      {shouldShowThumbnail && (
        <div
          className={cn("thumbnailOverlay", {
            loading: shouldShowLoadingIndicator,
          })}
          style={{ backgroundImage: `url(${thumbnailUrl})` }}
          onClick={(e) => {
            handleUserActivity();
            e.stopPropagation();
          }}
        >
          {shouldShowLoadingIndicator && (
            <div className={styles.loadingIndicator}>
              <div className={styles.loadingSpinner}></div>
            </div>
          )}
        </div>
      )}

      {/* Controls overlay */}
      <div
        className={cn("controlsOverlay", { visible: showControls })}
        onClick={(e) => {
          handleUserActivity();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          handleUserActivity();
        }}
        tabIndex="0"
        role="button"
        aria-label={playing ? "Pause video" : "Play video"}
        onKeyDown={(e) => e.key === "Enter" && handlePlayPause()}
      >
        {/* Play/Pause button */}
        <button
          className={cn("playPauseButton", {
            visible: showControls || (!playing && videoReady),
          })}
          onClick={(e) => {
            e.stopPropagation();
            handlePlayPause();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            handleUserActivity();
          }}
          aria-label={playing ? "Pause" : ended ? "Replay" : "Play"}
          tabIndex="0"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              handlePlayPause();
            }
          }}
        >
          {ended && !loop ? (
            <img src={IconReplay} alt="Replay" className={styles.replayIcon} />
          ) : playing ? (
            <img src={IconPause} alt="Pause" className={styles.pauseIcon} />
          ) : (
            <img src={IconPlay} alt="Play" className={styles.playIcon} />
          )}
        </button>

        {/* Progress bar */}
        <div
          className={cn("progressBarContainer", { visible: showControls })}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={cn("progressBar", { disabled: disableSeek })}
            onClick={(e) => {
              e.stopPropagation();
              if (!disableSeek) {
                handleSeek(e);
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation();

              if (disableSeek) {
                handleUserActivity();
                return;
              }

              const touch = e.touches[0];
              const progressBar = e.currentTarget;
              const rect = progressBar.getBoundingClientRect();
              const offsetX = touch.clientX - rect.left;
              const percentage = offsetX / rect.width;

              const effectiveStartTime = startTime || 0;
              const effectiveEndTime = endTime > 0 ? endTime : duration;

              const targetTime =
                effectiveStartTime +
                percentage * (effectiveEndTime - effectiveStartTime);

              const clampedTargetTime = Math.max(
                effectiveStartTime,
                Math.min(effectiveEndTime, targetTime)
              );

              playerRef.current?.seekTo(clampedTargetTime);

              const progressPercentage = percentage * 100;
              setProgress(Math.max(0, Math.min(100, progressPercentage)));

              setShowControls(true);
              handleUserActivity();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();

              if (disableSeek) return;

              if (e.key === "ArrowRight") {
                const newTime = Math.min(
                  endTime > 0 ? endTime : duration,
                  playerRef.current.getCurrentTime() + 5
                );
                if (playerRef.current) {
                  playerRef.current.seekTo(newTime);
                }
              } else if (e.key === "ArrowLeft") {
                const newTime = Math.max(
                  startTime,
                  playerRef.current.getCurrentTime() - 5
                );
                if (playerRef.current) {
                  playerRef.current.seekTo(newTime);
                }
              }
            }}
            tabIndex="0"
            role="slider"
            aria-label="Video progress"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={progress}
          >
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* WebSocket client for remocon functionality */}
      {isRemote && (() => {
        try {
          return (
            <SockJsClient
              url={`${getHost()}/academy/controller`}
              topics={[`/topic/${classroomId}/all`]}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
              onMessage={onMessageReceive}
              debug={false}
              options={{
                // Enable reconnection attempts
                reconnectEnabled: true,
                reconnectDelay: 3000
              }}
              heartbeat={5000} // Send heartbeat to keep connection alive
              headers={{
                // Add headers to help with connection stability
                'Cache-Control': 'no-cache'  
              }}
              subscribeHeaders={{
                // Add headers for subscription stability
                'Cache-Control': 'no-cache'
              }}
            />
          );
        } catch (error) {
          console.error("Error initializing SockJsClient:", error);
          return null;
        }
      })()}
    </div>
  );
}

export default YoutubePlayerWithRemocon;
